import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Pressable, ScrollView } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { IconCalendar, IconX } from '@tabler/icons-react-native';
import { useAtomValue, useSetAtom } from 'jotai';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Modal } from 'app/design/primitives/modal';
import { Button } from 'app/design/primitives/button';
import { useNavigation } from 'app/hooks/navigation';
import { useIsBlockingModalVisible } from 'app/hooks/use-is-blocking-modal-visible';
import { useMyPrivateProfile, useAcknowledgeFestivalReminder } from 'app/network/profile';
import { useUser } from 'app/network/user';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { presentFestivalReminderAtom } from 'app/atoms/ui';
import { isStartingAtom } from 'app/atoms';
import { navigationRef, subscribeNavigationState } from 'app/lib/root-navigation';
import { Event, track } from 'app/lib/analytics';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FestivalSavedDate } from './festival-profile-card';

export function FestivalReminder() {
  const focused = useIsFocused();
  return isWhiteLabel(WhiteLabel.Refrain) ? <RefrainFestivalReminder mode="banner" active={focused} /> : null;
}

/** Global prompt also handles openings through deep links to other screens. */
export function FestivalRenewalPrompt() {
  const starting = useAtomValue(isStartingAtom);
  const ready = useSyncExternalStore(
    subscribeNavigationState,
    () => navigationRef.isReady(),
    () => false,
  );
  return isWhiteLabel(WhiteLabel.Refrain) ? <RefrainFestivalReminder mode="modal" active={ready && !starting} /> : null;
}

function RefrainFestivalReminder({ mode, active }: { mode: 'banner' | 'modal'; active: boolean }) {
  const { t } = useTranslation('festival');
  const theme = useTheme();
  const navigation = useNavigation();
  const { data: user } = useUser();
  const profile = useMyPrivateProfile({
    enabled: active && user?.status === 'complete',
    refetchInterval: 60_000,
    staleTime: 0,
  });
  const reminder = profile.data?.festivalReminder;
  const blocked = useIsBlockingModalVisible();
  const setBlocking = useSetAtom(presentFestivalReminderAtom);
  const [openCycle, setOpenCycle] = useState<string | null>(null);
  const [dismissedCycle, setDismissedCycle] = useState<string | null>(null);
  const shownCycle = useRef<string | null>(null);
  const acknowledge = useAcknowledgeFestivalReminder();
  const open = Boolean(active && openCycle && openCycle === reminder?.cycleId);

  useEffect(() => {
    if (
      mode !== 'modal' ||
      !active ||
      blocked ||
      profile.isFetching ||
      !reminder?.showModal ||
      shownCycle.current === reminder.cycleId ||
      user?.status !== 'complete'
    )
      return;
    const timer = setTimeout(() => {
      setOpenCycle(reminder.cycleId);
      setBlocking(true);
    }, 0);
    return () => clearTimeout(timer);
  }, [active, blocked, mode, profile.isFetching, reminder, setBlocking, user?.status]);

  useEffect(() => {
    if (mode !== 'modal') return;
    setBlocking(open);
    return () => setBlocking(false);
  }, [mode, open, setBlocking]);

  const markShown = () => {
    if (!reminder || shownCycle.current === reminder.cycleId) return;
    shownCycle.current = reminder.cycleId;
    acknowledge.mutate({ cycleId: reminder.cycleId, event: 'modal_seen' });
    track(Event.FestivalPromptShown, { kind: reminder.kind });
  };
  const close = () => {
    if (reminder) shownCycle.current = reminder.cycleId;
    setOpenCycle(null);
    if (mode === 'modal') setBlocking(false);
  };
  const goToProfile = () => {
    close();
    navigation.navigate('main', { screen: 'profile', params: { screen: 'profile-overview', initial: false } });
  };
  const dismissBanner = () => {
    if (!reminder) return;
    setDismissedCycle(reminder.cycleId);
    acknowledge.mutate(
      { cycleId: reminder.cycleId, event: 'banner_dismissed' },
      { onError: () => setDismissedCycle(null) },
    );
  };

  if (!active || !reminder || user?.status !== 'complete') return null;
  const showBanner = mode === 'banner' && !blocked && reminder.showBanner && dismissedCycle !== reminder.cycleId;
  return (
    <>
      {showBanner ? (
        <Column
          backgroundColor="surface.info.subtle"
          borderRadius="radius.sm"
          paddingHorizontal="spacing.general.x6"
          paddingVertical="spacing.general.x4"
          gap="spacing.general.x4"
          maxWidth={440}
          width="100%"
          shadowColor="black"
          shadowOffset={{ width: 0, height: 4 }}
          shadowOpacity={0.25}
          shadowRadius={6.5}
          elevation={4}
          testID="festival-reminder-banner"
        >
          <Row alignItems="center" gap="spacing.general.x4">
            <Column
              backgroundColor="surface.info.default"
              borderRadius="radius.full"
              width={38}
              height={28}
              alignItems="center"
              justifyContent="center"
            >
              <IconCalendar size={24} color={theme.colors.white} strokeWidth={ICON_STROKE_WIDTH} />
            </Column>
            <Text variant="bodySmallMedium" flex={1}>
              {t('nextEdition')}
            </Text>
            <Pressable accessibilityRole="button" accessibilityLabel={t('close')} onPress={dismissBanner} hitSlop={12}>
              <IconX size={24} color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
          </Row>
          <Text variant="bodySmall">{t(reminder.kind === 'initial' ? 'initialMessage' : 'reminderMessage')}</Text>
          <Button
            variant="secondary"
            flexDirection="column"
            maxWidth="100%"
            alignSelf="flex-start"
            onPress={goToProfile}
          >
            {t('updateDate')}
          </Button>
        </Column>
      ) : null}
      <Modal open={open} transparent animationType="fade" onClose={close} onShow={markShown}>
        <Column
          backgroundColor="surface.default"
          borderRadius="radius.lg"
          width="92%"
          maxWidth={600}
          maxHeight="90%"
          testID="festival-renewal-modal"
        >
          <Row justifyContent="flex-end" padding="spacing.general.x6">
            <Pressable accessibilityRole="button" accessibilityLabel={t('close')} onPress={close} hitSlop={12}>
              <IconX size={32} color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
          </Row>
          <ScrollView>
            <Column paddingHorizontal="spacing.general.x6" paddingBottom="spacing.general.x6" gap="spacing.general.x6">
              <Column
                width={64}
                height={64}
                alignItems="center"
                justifyContent="center"
                backgroundColor="surface.secondary-neutral"
                borderRadius="radius.md"
              >
                <IconCalendar size={32} color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} />
              </Column>
              <Text variant="bodyXLargeStrong">
                {t(reminder.kind === 'initial' ? 'initialTitle' : 'reminderTitle')}
              </Text>
              <Text variant="bodyLarge">{t(reminder.kind === 'initial' ? 'initialMessage' : 'reminderMessage')}</Text>
              {profile.data?.festivalStartDate ? (
                <Column
                  borderWidth={1}
                  borderColor="border.secondary"
                  borderRadius="radius.lg"
                  padding="spacing.general.x4"
                >
                  <FestivalSavedDate value={profile.data.festivalStartDate} />
                </Column>
              ) : null}
            </Column>
          </ScrollView>
          <Row padding="spacing.general.x6" justifyContent="flex-end" gap="spacing.general.x4" flexWrap="wrap">
            <Button variant="secondary" flexDirection="column" maxWidth="100%" onPress={close}>
              {t('later')}
            </Button>
            <Button variant="primary" flexDirection="column" maxWidth="100%" onPress={goToProfile}>
              {t('updateDate')}
            </Button>
          </Row>
        </Column>
      </Modal>
    </>
  );
}
